/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { defineStore } from "pinia";
import { searchApi } from "../AlfrescoApi";
import { useConfigStore } from "./config";

/**
 * A Pinia store definition for managing search state and operations.
 *
 * Provides state, getters, and actions to handle search-related functionality,
 * including query definitions, result pagination, sorting, facets, and context.
 *
 * Store ID: "SearchStore"
 *
 * State:
 * - language: Specifies the query language, default is "afts".
 * - userQuery: The raw user input for search.
 * - aftsQuery: Reserved for AFTS-specific queries.
 * - fullQuery: The full search query composed of user query and additional parameters.
 * - paging: Contains pagination properties like `maxItems` and `skipCount`.
 * - include: Specifies the fields to be included in the results, e.g., properties and path.
 * - sort: Custom sort criteria applied to the search results.
 * - defaultSort: Default sorting configured by field and order.
 * - spellcheck: Reserved for enabling/disabling spellcheck in the search.
 * - filterQueries: Filters applied to limit search results based on specific criteria.
 * - facetQueries: Queries for retrieving facet-related data.
 * - facets: Configurations for available facets such as field, label, and minimum count.
 * - fields: Custom fields selected for filtering or querying.
 * - context: Contains metadata about the current search result context.
 * - entries: The list of search results.
 * - pagination: Information about the result pagination state.
 *
 * Getters:
 * - facetFields: Extracts and returns facets from the store state.
 *
 * Actions:
 * - reset: Resets the store state to its initial configuration using default values and external configurations.
 * - defineUserQuery(userQuery): Constructs and updates the search query using the provided user input.
 * - queryStore(searchInput, page): Executes the configured search query and updates the store with the results.
 * - addSortField(sortField): Adds a custom sort criterion.
 * - removeSortField: Clears the existing sort configuration.
 * - addFilterQuery(bucket): Adds a new filter query if it does not already exist.
 * - removeFilterQuery(bucket): Removes a specific filter query based on the provided bucket.
 * - addField(bucket): Adds a new field to the fields array if it does not already exist.
 * - removeField(bucket): Removes a specific field from the fields array.
 */
export const useSearchStore = defineStore({
  id: "SearchStore",
  state: () => ({
    language: "afts",
    userQuery: "",
    aftsQuery: "",
    fullQuery: "",
    paging: {
      maxItems: "15",
      skipCount: "0",
    },
    include: ["properties", "allowableOperations", "permissions", "path"],
    sort: null,
    defaultSort: [{ type: "FIELD", field: "score", ascending: false }],
    spellcheck: null,
    filterQueries: [],
    facetQueries: [],
    facets: [],
    fields: [],
    context: null,
    entries: [],
    pagination: {},
    isExactSearchActivated: false,
  }),
  getters: {
    facetFields(state) {
      return { facetFields: state.facets };
    },
  },
  actions: {
    setIsExactSearchActivated(value) {
      this.isExactSearchActivated = value;
    },
    reset() {
      const config = useConfigStore();

      this.language = "afts";
      this.userQuery = "";
      this.fullQuery = "";
      this.filterQueries = [...config.SEARCH_BASE_FILTER_QUERIES];
      this.facetQueries = [...config.SEARCH_FACET_QUERIES];
      this.facets = [...config.SEARCH_FACET];

      this.fields = [];
      this.context = null;
      this.entries = [];
      this.pagination = {};
    },
    defineUserQuery(userQuery) {
      const config = useConfigStore();
      let iQuery = null;
      if (!this.isExactSearchActivated) {
        iQuery = config.SEARCH_USER_QUERY.replaceAll("%s", userQuery);
        // Avoid double wildcards (e.g. user input "*" + template "*" → "**")
        iQuery = iQuery.replaceAll("**", "*");
      } else {
        let query = config.SEARCH_USER_QUERY.replaceAll("*", "");
        iQuery = query.replaceAll("%s", userQuery);
      }
      this.language = "afts";
      this.userQuery = userQuery;
      this.fullQuery = iQuery;
    },

    queryStore(searchInput, page, customQuery = null) {
      if (!searchInput) {
        return Promise.resolve();
      }

      const maxItems = parseInt(this.paging.maxItems) || 15;
      this.paging.skipCount = page ? page * maxItems : 0;

      // If customQuery is provided, use it as the main query
      // Otherwise, use the default defineUserQuery behavior
      if (customQuery) {
        this.fullQuery = customQuery;
        this.userQuery = searchInput;
      } else {
        this.defineUserQuery(searchInput);
      }

      // Build final filter queries
      // When using customQuery (user views), only keep pm:hidden filter to avoid conflicts
      const finalFilterQueries = customQuery ? [] : [...this.filterQueries];

      // Add interactive field filters (TYPE: facets) as filterQueries for Solr cache efficiency
      for (const field of this.fields) {
        if (!finalFilterQueries.some((f) => f.query === field.query)) {
          finalFilterQueries.push({ query: field.query });
        }
      }

      if (!finalFilterQueries.some((f) => f.query === "-ASPECT:'pm:hidden'")) {
        finalFilterQueries.push({ query: "-ASPECT:'pm:hidden'" });
      }

      let optsQuery = {
        query: {
          language: this.language,
          query: this.fullQuery,
        },
        include: this.include,
        paging: this.paging,
        filterQueries: finalFilterQueries.map((f) => ({ query: f.query })),
        facetQueries: this.facetQueries,
        facetFields: {
          facets: this.facets.map((f) => {
            return {
              field: f.field,
              mincount: f.mincount,
              label: f.label,
            };
          }),
        },
        sort: this.sort || this.defaultSort,
        facetFormat: "V2",
      };
      return searchApi.search(optsQuery).then((results) => {
        this.context = results.list.context;
        if (page > 0) {
          this.entries = [...this.entries, ...results.list.entries];
        } else {
          this.entries = results.list.entries;
        }

        this.pagination = results.list.pagination;
      });
    },
    searchForExport(nbrToExport) {
      let customPaging = { ...this.paging };
      customPaging.maxItems = nbrToExport;

      const finalFilterQueries = [...this.filterQueries];
      for (const field of this.fields) {
        if (!finalFilterQueries.some((f) => f.query === field.query)) {
          finalFilterQueries.push({ query: field.query });
        }
      }
      if (!finalFilterQueries.some((f) => f.query === "-ASPECT:'pm:hidden'")) {
        finalFilterQueries.push({ query: "-ASPECT:'pm:hidden'" });
      }

      let optsQuery = {
        query: {
          language: this.language,
          query: this.fullQuery,
        },
        include: this.include,
        paging: customPaging,
        filterQueries: finalFilterQueries.map((f) => ({ query: f.query })),
        facetQueries: this.facetQueries,
        facetFields: {
          facets: this.facets.map((f) => {
            return {
              field: f.field,
              mincount: f.mincount,
              label: f.label,
            };
          }),
        },
        sort: this.sort || this.defaultSort,
        facetFormat: "V2",
      };
      return searchApi.search(optsQuery).then((results) => {
        return results;
      });
    },
    addSortField(sortField) {
      this.sort = sortField;
    },
    removeSortField() {
      this.sort = null;
    },
    /**
     *
     * @param bucket {query: ".." , label: ".."}
     * @returns {boolean}
     */
    addFilterQuery(bucket) {
      if (!bucket.query) {
        return false;
      }
      if (!this.filterQueries.find((field) => field.query === bucket.query)) {
        this.filterQueries.push({
          query: bucket.query,
          label: bucket.label,
        });

        return true;
      }
      return false;
    },
    removeFilterQuery(bucket) {
      this.filterQueries = this.filterQueries.filter((field) => {
        if (bucket.query.startsWith("(SITE:")) {
          if (
            field.label &&
            (field.label.startsWith("Dossier :") ||
              field.label.startsWith("Folder :"))
          ) {
            return false;
          }
        }
        return field.query !== bucket.query;
      });
    },
    /**
     *
     * @param bucket {query: ".." , label: ".."}
     * @returns {boolean}
     */
    addField(bucket) {
      if (!this.fields.find((field) => field.query === bucket.query)) {
        this.fields.push({
          query: bucket.query,
          label: bucket.label,
        });

        return true;
      }
      return false;
    },
    removeField(bucket) {
      this.fields = this.fields.filter((field) => field.query !== bucket.query);
    },
  },
});
